import { Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';

import i18n from './config';
import { Skeleton } from '@shared/components/ui/skeleton';

interface I18nProviderProps {
  children: React.ReactNode;
}

/**
 * Loading fallback while translations are loading.
 * Uses the same skeleton layout as TenantLoadingFallback for visual consistency.
 */
function I18nLoadingFallback() {
  return (
    <div className="min-h-screen bg-card flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border max-w-2xl mx-auto w-full">
        <Skeleton className="h-8 w-12" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-8 rounded-full" />
      </header>
      <main className="flex-1 p-6 max-w-2xl mx-auto w-full">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-48 mx-auto mb-3" />
          <Skeleton className="h-6 w-32 mx-auto" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * I18n Provider component that wraps the application
 * Language priority: localStorage (saved preference) → browser language → 'en' fallback
 * Handled automatically by i18next LanguageDetector in config.ts
 */
export function I18nProvider({ children }: I18nProviderProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<I18nLoadingFallback />}>
        {children}
      </Suspense>
    </I18nextProvider>
  );
}

export default I18nProvider;
